import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Database,
    Plus,
    Trash2,
    X,
    AlertTriangle,
    RefreshCw,
    Search,
    SearchX,
    Eye,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    Calendar,
    Download,
    Building2,
    Edit,
    ToggleLeft,
    ToggleRight
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { companySizeService } from '../../../api/crm/config/configService';
import { formatDate } from '../../../utils/stringUtils';
import { exportToCSV, generateExportFilename } from '../../../utils/exportUtils';

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 100;
const DESC_MAX_LENGTH = 200;

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const CompanySizeManagement = () => {
    const queryClient = useQueryClient();

    // Local States
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const debouncedSearch = useDebounce(searchTerm, 300);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('create'); // 'create' or 'edit'
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', isActive: true });
    const [formErrors, setFormErrors] = useState({});
    const [touched, setTouched] = useState({});

    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingItem, setViewingItem] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [bulkDeleteError, setBulkDeleteError] = useState('');

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    // Reset pagination on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, statusFilter, dateRange]);

    // Main Query
    const { data: companySizesData, isLoading } = useQuery({
        queryKey: ['companySizes', { search: debouncedSearch, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate }],
        queryFn: () => companySizeService.getAll({
            search: debouncedSearch,
            ...(statusFilter !== 'all' && { isActive: statusFilter === 'active' }),
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        }),
    });
    const companySizes = companySizesData?.data || [];

    // Mutations
    const createMutation = useMutation({
        mutationFn: (data) => companySizeService.create(data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['companySizes'] });
            toast.success(res?.message || 'Company size created successfully');
            closeModal();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to create company size');
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => companySizeService.update(id, data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['companySizes'] });
            toast.success(res?.message || 'Company size updated successfully');
            closeModal();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to update company size');
        }
    });

    const toggleStatusMutation = useMutation({
        mutationFn: (id) => companySizeService.toggleStatus(id),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['companySizes'] });
            toast.success(res?.message || 'Status updated successfully');
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to update status');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => companySizeService.delete(id),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['companySizes'] });
            toast.success(res?.message || 'Company size deleted successfully');
            setShowDeleteModal(false);
            setDeletingItem(null);
            setSelectedRows(prev => {
                const updated = new Set(prev);
                if (deletingItem) updated.delete(deletingItem._id);
                return updated;
            });
        },
        onError: (err) => {
            setDeleteError(err?.response?.data?.message || err?.message || 'Failed to delete item');
        }
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: (ids) => companySizeService.bulkDelete(ids),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['companySizes'] });
            toast.success(res?.message || 'Selected company sizes deleted successfully');
            setShowBulkDeleteModal(false);
            setSelectedRows(new Set());
        },
        onError: (err) => {
            setBulkDeleteError(err?.response?.data?.message || err?.message || 'Failed to bulk delete items');
        }
    });

    // Helpers
    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getItemDate = (item) => {
        if (item.createdAt) return new Date(item.createdAt);
        if (item._id || item.id) {
            const id = item._id || item.id;
            if (typeof id === 'string' && id.length === 24) {
                const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
                return new Date(timestamp);
            }
        }
        return null;
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
        return sortConfig.direction === 'asc' ?
            <ArrowUp className="h-4 w-4 text-agrosaf-green" /> :
            <ArrowDown className="h-4 w-4 text-agrosaf-green" />;
    };

    const sortedData = useMemo(() => {
        let sortableData = [...companySizes];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (sortConfig.key === 'createdAt') {
                    const aDate = getItemDate(a);
                    const bDate = getItemDate(b);
                    aVal = aDate ? aDate.getTime() : 0;
                    bVal = bDate ? bDate.getTime() : 0;
                } else if (sortConfig.key === 'updatedAt') {
                    aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                } else {
                    aVal = String(aVal || '').toLowerCase();
                    bVal = String(bVal || '').toLowerCase();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableData;
    }, [companySizes, sortConfig]);

    // Pagination slice
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageLimit;
        return sortedData.slice(startIndex, startIndex + pageLimit);
    }, [sortedData, currentPage, pageLimit]);

    const totalPages = Math.ceil(sortedData.length / pageLimit) || 1;

    // Row selection checkboxes
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const currentIds = paginatedData.map(row => row._id);
            setSelectedRows(prev => {
                const next = new Set(prev);
                currentIds.forEach(id => next.add(id));
                return next;
            });
        } else {
            const currentIds = paginatedData.map(row => row._id);
            setSelectedRows(prev => {
                const next = new Set(prev);
                currentIds.forEach(id => next.delete(id));
                return next;
            });
        }
    };

    const handleSelectRow = (id) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // Modal Helpers
    const openCreateModal = () => {
        setModalType('create');
        setEditingItem(null);
        setFormData({ name: '', description: '', isActive: true });
        setFormErrors({});
        setTouched({});
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setModalType('edit');
        setEditingItem(item);
        setFormData({
            name: item.name,
            description: item.description || '',
            isActive: item.isActive
        });
        setFormErrors({});
        setTouched({});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '', description: '', isActive: true });
        setFormErrors({});
        setTouched({});
    };

    const validateName = (name) => {
        if (!name || !name.trim()) {
            return 'Name is required';
        }
        const trimmed = name.trim();
        if (trimmed.length < NAME_MIN_LENGTH) {
            return `Name must be at least ${NAME_MIN_LENGTH} characters`;
        }
        if (trimmed.length > NAME_MAX_LENGTH) {
            return `Name cannot exceed ${NAME_MAX_LENGTH} characters`;
        }
        if (!/^[a-zA-Z0-9\s\-&+/(),. ]+$/.test(trimmed)) {
            return 'Name can only contain letters, numbers, spaces, hyphens, ampersands, plus signs, forward slashes, parentheses, commas, and dots';
        }
        return '';
    };

    const validateDescription = (desc) => {
        if (desc && desc.trim().length > DESC_MAX_LENGTH) {
            return `Description cannot exceed ${DESC_MAX_LENGTH} characters`;
        }
        return '';
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        const errors = { ...formErrors };
        if (field === 'name') {
            errors.name = validateName(formData.name);
        } else if (field === 'description') {
            errors.description = validateDescription(formData.description);
        }
        setFormErrors(errors);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const errors = {
            name: validateName(formData.name),
            description: validateDescription(formData.description)
        };

        if (errors.name || errors.description) {
            setFormErrors(errors);
            setTouched({ name: true, description: true });
            return;
        }

        if (modalType === 'create') {
            createMutation.mutate(formData);
        } else {
            updateMutation.mutate({ id: editingItem._id, data: formData });
        }
    };

    // View Details Modal
    const handleViewDetails = (item) => {
        setViewingItem(item);
        setShowViewModal(true);
    };

    // Single Delete Modal
    const handleDeleteClick = (item) => {
        setDeletingItem(item);
        setDeleteError('');
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (deletingItem) {
            deleteMutation.mutate(deletingItem._id);
        }
    };

    // Bulk Delete
    const handleConfirmBulkDelete = () => {
        const idsArray = Array.from(selectedRows);
        bulkDeleteMutation.mutate(idsArray);
    };

    // Exporting
    const handleExportClick = () => {
        setShowExportModal(true);
    };

    const handleExportCurrentPage = (format) => {
        setExportLoading(true);
        try {
            const dataToExport = selectedRows.size > 0
                ? companySizes.filter(row => selectedRows.has(row._id))
                : companySizes;

            const columns = [
                { header: 'Company Size Code', key: 'companySizeCode' },
                { header: 'Name', key: 'name' },
                { header: 'Description', key: 'description' },
                { header: 'Status', key: 'isActive', callback: (val) => val ? 'Active' : 'Inactive' },
                { header: 'Created At', key: 'createdAt', callback: (val) => formatDate(val) }
            ];

            const filename = generateExportFilename('company_sizes', format);
            exportToCSV(dataToExport, columns, filename);
            toast.success('Company sizes exported successfully');
            setShowExportModal(false);
        } catch (error) {
            toast.error('Export failed');
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div>
            {/* Search and Add Button */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div className="flex-1 w-full flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search company sizes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                        />
                        {searchTerm && (
                            <button
                                onClick={handleClearSearch}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent min-w-[150px]"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                    </select>
                    <DateRangeFilter
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        onChange={setDateRange}
                    />
                </div>
                <div className="flex items-center gap-2">
                    {selectedRows.size > 0 && (
                        <Button
                            variant="danger"
                            icon={<Trash2 className="h-4 w-4" />}
                            onClick={() => setShowBulkDeleteModal(true)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete ({selectedRows.size})
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        icon={<Download className="h-4 w-4" />}
                        onClick={handleExportClick}
                        disabled={exportLoading}
                    >
                        {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                    </Button>
                    <Button
                        variant="primary"
                        icon={<Plus className="h-4 w-4" />}
                        onClick={openCreateModal}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    >
                        Add Company Size
                    </Button>
                </div>
            </div>

            {/* Table or loader */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
                    <span className="ml-3 text-gray-600">Loading...</span>
                </div>
            ) : companySizes.length === 0 ? (
                <div className="text-center py-12">
                    {searchTerm.trim() ? (
                        <>
                            <SearchX className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
                            <p className="text-gray-600 mb-4">No company sizes found matching "{searchTerm}"</p>
                            <Button variant="outline" onClick={handleClearSearch} className="text-gray-600">
                                Clear Search
                            </Button>
                        </>
                    ) : (
                        <>
                            <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Sizes Found</h3>
                            <p className="text-gray-600 mb-4">Create your first company size to get started.</p>
                            <Button
                                variant="primary"
                                icon={<Plus className="h-4 w-4" />}
                                onClick={openCreateModal}
                                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                            >
                                Add Company Size
                            </Button>
                        </>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-4">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                        checked={paginatedData.length > 0 && paginatedData.every(row => selectedRows.has(row._id))}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('companySizeCode')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Company Size Code</span>
                                        {getSortIndicator('companySizeCode')}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('name')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Company Size Name</span>
                                        {getSortIndicator('name')}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('description')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Description</span>
                                        {getSortIndicator('description')}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('isActive')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Status</span>
                                        {getSortIndicator('isActive')}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('createdAt')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Created At</span>
                                        {getSortIndicator('createdAt')}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                                    onClick={() => handleSort('updatedAt')}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Updated At</span>
                                        {getSortIndicator('updatedAt')}
                                    </div>
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedData.map((row) => (
                                <tr key={row._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                            checked={selectedRows.has(row._id)}
                                            onChange={() => handleSelectRow(row._id)}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded inline-block">
                                            {row.companySizeCode || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate capitalize" title={row.name}>
                                            {row.name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500 max-w-[200px] truncate capitalize" title={row.description || '-'}>
                                            {row.description || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.isActive
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {row.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDate(getItemDate(row)) || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {row.updatedAt ? formatDate(new Date(row.updatedAt)) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-3">
                                            <button
                                                onClick={() => handleViewDetails(row)}
                                                className="text-gray-600 hover:text-gray-900"
                                                title="View Details"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => openEditModal(row)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title={row.isUndeletable ? 'Edit Name & Description' : 'Edit'}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            {!row.isUndeletable && (
                                                <>
                                                    <button
                                                        onClick={() => toggleStatusMutation.mutate(row._id)}
                                                        className={row.isActive ? 'text-orange-500 hover:text-orange-700' : 'text-green-600 hover:text-green-800'}
                                                        title={row.isActive ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {row.isActive ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(row)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </>
                                            )}
                                            {row.isUndeletable && (
                                                <span className="text-gray-400 text-xs italic">System Required</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {sortedData.length > 10 && (
                        <div className="mt-4 mb-4 px-6">
                            <PaginationControls
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                limit={pageLimit}
                                onLimitChange={(lim) => {
                                    setPageLimit(lim);
                                    setCurrentPage(1);
                                }}
                                totalCount={sortedData.length}
                                limitOptions={[10, 25, 50, 100]}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* CREATE / EDIT MODAL */}
            {showModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={closeModal}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {modalType === 'create' ? 'Add' : 'Edit'} Company Size
                                </h3>
                                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleFormSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val.length <= NAME_MAX_LENGTH) {
                                                setFormData({ ...formData, name: val });
                                                if (formErrors.name) setFormErrors(prev => ({ ...prev, name: '' }));
                                            }
                                        }}
                                        onBlur={() => handleBlur('name')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${formErrors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter company size name (e.g. 1-10, 11-50)"
                                    />
                                    <div className="flex justify-between mt-1">
                                        <div>
                                            {formErrors.name && (
                                                <p className="text-red-500 text-sm">{formErrors.name}</p>
                                            )}
                                        </div>
                                        <p className={`text-xs ${formData.name?.length >= NAME_MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                                            {formData.name?.length || 0} / {NAME_MAX_LENGTH}
                                        </p>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Min {NAME_MIN_LENGTH} characters. Letters, numbers, spaces, hyphens, ampersands, plus, slashes, parentheses, commas, dots allowed.
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val.length <= DESC_MAX_LENGTH) {
                                                setFormData({ ...formData, description: val });
                                                if (formErrors.description) setFormErrors(prev => ({ ...prev, description: '' }));
                                            }
                                        }}
                                        onBlur={() => handleBlur('description')}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${formErrors.description ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                                            }`}
                                        rows="3"
                                        placeholder="Enter description"
                                    />
                                    <div className="flex justify-between mt-1">
                                        <div>
                                            {formErrors.description && (
                                                <p className="text-red-500 text-sm">{formErrors.description}</p>
                                            )}
                                        </div>
                                        <p className={`text-xs ${formData.description?.length >= DESC_MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                                            {formData.description?.length || 0} / {DESC_MAX_LENGTH}
                                        </p>
                                    </div>
                                </div>

                                {!(modalType === 'edit' && editingItem?.isUndeletable) && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                        <div className="flex items-center space-x-3">
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:ring-offset-2 ${formData.isActive ? 'bg-agrosaf-green' : 'bg-gray-200'
                                                    }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.isActive ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                />
                                            </button>
                                            <span className={`text-sm font-medium ${formData.isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                                    <Button type="button" variant="outline" onClick={closeModal} className="text-gray-700">
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                                        disabled={createMutation.isPending || updateMutation.isPending}
                                    >
                                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (modalType === 'create' ? 'Create' : 'Update')}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* DETAILS MODAL */}
            {showViewModal && viewingItem && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowViewModal(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-green-50 rounded-lg">
                                        <Building2 className="h-6 w-6 text-agrosaf-green" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Company Size Details</h3>
                                        <p className="text-xs text-gray-500">System Configuration Master</p>
                                    </div>
                                </div>
                                <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                                        Company Size Code
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900 bg-gray-50 px-2.5 py-1.5 rounded-md border border-gray-100 block">
                                        {viewingItem.companySizeCode}
                                    </span>
                                </div>

                                <div>
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                                        Name
                                    </span>
                                    <span className="text-sm font-medium text-gray-900 block bg-gray-50 px-2.5 py-1.5 rounded-md border border-gray-100 capitalize">
                                        {viewingItem.name}
                                    </span>
                                </div>

                                <div>
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                                        Description
                                    </span>
                                    <div className="text-sm text-gray-600 bg-gray-50 px-2.5 py-2.5 rounded-md border border-gray-100 min-h-[60px] whitespace-pre-wrap">
                                        {viewingItem.description || 'No description provided.'}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                                            Status
                                        </span>
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${viewingItem.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {viewingItem.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                                            System Config
                                        </span>
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${viewingItem.isUndeletable
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {viewingItem.isUndeletable ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
                                    <div>
                                        <span>Created At:</span>
                                        <span className="block font-medium text-gray-600 mt-0.5">
                                            {formatDate(getItemDate(viewingItem))}
                                        </span>
                                    </div>
                                    <div>
                                        <span>Last Updated:</span>
                                        <span className="block font-medium text-gray-600 mt-0.5">
                                            {formatDate(new Date(viewingItem.updatedAt))}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end">
                                <Button variant="outline" onClick={() => setShowViewModal(false)} className="text-gray-700">
                                    Close
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {showDeleteModal && deletingItem && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowDeleteModal(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
                            <div className="flex items-center justify-center mb-4 text-red-600">
                                <AlertTriangle className="h-12 w-12" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Delete Company Size</h3>
                            <p className="text-sm text-gray-600 text-center mb-4">
                                Are you sure you want to delete <span className="font-semibold">{deletingItem.name}</span>? This action cannot be undone.
                            </p>
                            {deleteError && (
                                <p className="text-red-500 text-sm text-center mb-4 bg-red-50 py-2 rounded">{deleteError}</p>
                            )}
                            <div className="flex justify-center space-x-3">
                                <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="text-gray-700" disabled={deleteMutation.isPending}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleConfirmDelete}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                    disabled={deleteMutation.isPending}
                                >
                                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* BULK DELETE MODAL */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowBulkDeleteModal(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
                            <div className="flex items-center justify-center mb-4 text-red-600">
                                <AlertTriangle className="h-12 w-12" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Bulk Delete Company Sizes</h3>
                            <p className="text-sm text-gray-600 text-center mb-4">
                                Are you sure you want to delete <span className="font-semibold">{selectedRows.size}</span> selected company sizes? This action cannot be undone.
                            </p>
                            {bulkDeleteError && (
                                <p className="text-red-500 text-sm text-center mb-4 bg-red-50 py-2 rounded">{bulkDeleteError}</p>
                            )}
                            <div className="flex justify-center space-x-3">
                                <Button variant="outline" onClick={() => setShowBulkDeleteModal(false)} className="text-gray-700" disabled={bulkDeleteMutation.isPending}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleConfirmBulkDelete}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                    disabled={bulkDeleteMutation.isPending}
                                >
                                    {bulkDeleteMutation.isPending ? 'Deleting...' : 'Delete All'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* EXPORT MODAL */}
            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExportCurrentPage={handleExportCurrentPage}
                totalCount={selectedRows.size > 0 ? selectedRows.size : companySizes.length}
                selectedCount={selectedRows.size}
            />
        </div>
    );
};

export default CompanySizeManagement;
